import { useState, useEffect, useMemo } from 'react';
import { FaClock, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaPauseCircle } from 'react-icons/fa';

/**
 * SlaCountdownChip
 *
 * Compact, live-ticking SLA deadline chip for use on Kanban cards and list
 * rows. Uses the `sla` snapshot already embedded in the action items list
 * response so no extra API call is needed.
 *
 * Colour semantics:
 *   ● blue     — on-track, plenty of time remaining
 *   ● amber    — warned (within warning threshold set by policy)
 *   ● red      — breached
 *   ● green    — resolved within SLA
 *   ● yellow   — SLA clock is paused (on-hold ticket)
 *
 * The component recalculates "time remaining" every 60 seconds so the text
 * stays accurate without triggering a server fetch.
 *
 * @param {Object}  sla        - SLA snapshot from the action items list response
 * @param {string}  [variant]  - "card" (default) → one-line chip; "row" → with label text
 */
const SlaCountdownChip = ({ sla, variant = 'card' }) => {
    // Tick every 60 seconds so "time remaining" text stays up to date.
    const [tick, setTick] = useState(0);
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => {
            setNow(Date.now());
            setTick(t => t + 1);
        }, 60_000);
        return () => clearInterval(id);
    }, []);

    // Recompute time remaining on every render triggered by the 60-second tick.
    // `tick` is listed as a dependency so the memo re-runs each time the interval
    // fires, keeping the displayed countdown accurate without an extra API call.
    const remainMs = useMemo(() => {
        if (!sla) return null;
        const deadline = sla.resolutionDeadline ?? sla.responseDeadline;
        return deadline ? new Date(deadline) - now : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sla, tick, now]);

    if (!sla) return null;

    /**
     * Format a positive duration (milliseconds) as a human-readable string:
     *   < 2 hours  → "Xh Ym"  (shows minutes for precision when close to breach)
     *   < 2 days   → "Xh"     (hours-only once past the 2-hour mark)
     *   otherwise  → "Xd Yh"  (days + remaining hours)
     */
    const formatRemaining = (ms) => {
        if (ms <= 0) return 'Overdue';
        const totalMins = Math.floor(ms / 60_000);
        const hours = Math.floor(totalMins / 60);
        const mins  = totalMins % 60;
        if (hours < 2) return hours === 0 ? `${mins}m` : `${hours}h ${mins}m`;
        if (hours < 48) return `${hours}h`;
        const days = Math.floor(hours / 24);
        const remainHours = hours % 24;
        return `${days}d ${remainHours}h`;
    };

    // Prefer resolution SLA; fall back to response SLA when resolution is not set.
    const activeDeadline  = sla.resolutionDeadline ?? sla.responseDeadline;
    const isCompleted     = !!sla.resolvedAt;
    const isBreached      = sla.resolutionBreached || sla.responseBreached;
    const isWarned        = sla.resolutionWarned   || sla.responseWarned;
    const isPaused        = sla.isPaused;

    // remainMs is computed by the useMemo above (tick dependency keeps it fresh).
    const isOverdue       = remainMs !== null && remainMs <= 0;

    // Determine display variant.
    let chipClass;
    let icon;
    let label;

    if (isCompleted) {
        chipClass = 'sla-chip sla-chip--met';
        icon = <FaCheckCircle />;
        label = variant === 'row' ? 'SLA Met' : 'Met';
    } else if (isPaused) {
        chipClass = 'sla-chip sla-chip--paused';
        icon = <FaPauseCircle />;
        label = variant === 'row' ? 'SLA Paused' : 'Paused';
    } else if (isBreached || isOverdue) {
        chipClass = 'sla-chip sla-chip--breached';
        icon = <FaTimesCircle />;
        label = variant === 'row' ? 'SLA Breached' : 'Breached';
    } else if (isWarned) {
        chipClass = 'sla-chip sla-chip--warning';
        icon = <FaExclamationTriangle />;
        label = remainMs != null ? formatRemaining(remainMs) : 'Due soon';
    } else {
        chipClass = 'sla-chip sla-chip--active';
        icon = <FaClock />;
        label = remainMs != null ? formatRemaining(remainMs) : '–';
    }

    return (
        <span
            className={chipClass}
            title={
                activeDeadline
                    ? `SLA resolution deadline: ${new Date(activeDeadline).toLocaleString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                      })}`
                    : 'SLA tracked'
            }
        >
            {icon}
            <span className="sla-chip-text">{label}</span>
        </span>
    );
};

export default SlaCountdownChip;
